import { createThunk, model, Model } from 'mst-collection';
import { castToSnapshot, getRoot, types } from 'mobx-state-tree';
import { format, parseISO } from 'date-fns';

import {
  Api,
  ICredits,
  ISendTrackFeedbackParams,
  TrackFeedbackSubmitOptionEnum,
} from '@api';
import { TrackFeedbacksListModel } from './TrackFeedbacksListModel';
import { CreditsModel } from './CreditsModel';
import { CreditOffersListModel } from './CreditOffersListModel';
import { formatCurrencyToGBP } from '@utils';
import type { RootModelType } from '../RootModel';
import { TariffPlanCodeEnum } from '@constants';

export class TrackFeedbacks extends Model({
  tracks: types.optional(TrackFeedbacksListModel, {}),
  isNewPeriod: false,
  credits: types.maybeNull(CreditsModel),
  creditOffers: types.optional(CreditOffersListModel, {}),
}) {
  setIsNewPeriod(isNewPeriod: boolean) {
    this.isNewPeriod = isNewPeriod;
  }

  setCredits(credits: ICredits) {
    this.credits = castToSnapshot(credits);
  }

  get isAvailableForCurrentPlan(): boolean {
    return !(
      getRoot<RootModelType>(this).viewer.currentUser?.tariffPlan
        .id === TariffPlanCodeEnum.Trial
    );
  }

  get nextDateFreeFeedback(): string {
    const freeTrack = this.tracks.asArray.find(
      (e) =>
        e.feedbackOptionType === TrackFeedbackSubmitOptionEnum.Free,
    );
    return freeTrack?.nextDateFormattedTime ?? '';
  }

  creditOfferById(id: number) {
    return this.creditOffers.findById(id);
  }

  getFormattedDate(date: string) {
    return format(parseISO(date), 'd MMMM yyyy');
  }

  getFormattedPrice(price: number | null) {
    if (!price) {
      return '';
    }
    return formatCurrencyToGBP(price.toString());
  }

  sendTrackFeedback = createThunk(
    (params: ISendTrackFeedbackParams) => {
      return async function sendTrackFeedback(this: TrackFeedbacks) {
        await Api.Tracks.sendTrackFeedback(params);
        this.setIsNewPeriod(false);
        this.credits?.decreaseCount();
        await this.tracks.fetch.run();
      };
    },
  );

  fetchStatus = createThunk(() => {
    return async function fetchStatus(this: TrackFeedbacks) {
      const res = await Api.Tracks.getTrackFeedbacks();

      this.setCredits(res.data.credits);

      this.setIsNewPeriod(res.data.isNewPeriod);
    };
  });
}

export const TrackFeedbacksModel = model(TrackFeedbacks);
